import {
  AlgonomyClient,
  Map_String,
  Placement,
  RenderRequestDto,
} from "../../_generated";
import { safeDefine } from "@breuni-ui/safe-define";
import { RecoLoader } from "./RecoLoader";
import { neitherNullNorUndefined } from "../utils/neitherNullNorUndefined";
import { getBaseUrl } from "../utils/getBaseUrl";
import AlgonomyCookieStringStorage from "../AlgonomyCookieStringStorage";

const recoLoaderTagName = "reco-multi-instance-reco-loader" as const;

export type VariantenWechselDetails = {
  syan: string;
  farbId?: string;
  artikelId?: string;
};

export const variantenWechselEvent = "bewe:ents:variantenwechsel" as const;

declare global {
  interface DocumentEventMap {
    [variantenWechselEvent]: CustomEvent<VariantenWechselDetails>;
  }
}

class MultiInstanceRecoLoader extends RecoLoader {
  private recoLoaders: MultiInstanceRecoLoader[];
  private readonly componentDisconnectedAbortController: AbortController;
  private algonomyClient: AlgonomyClient | undefined;

  constructor() {
    super();
    this.recoLoaders = [];
    this.componentDisconnectedAbortController = new AbortController();
  }

  private async setupRecommendationsLoading(): Promise<void> {
    this.recoLoaders = Array.from(
      document.querySelectorAll<MultiInstanceRecoLoader>(recoLoaderTagName),
    );
    if (this.determineLeader()) {
      if (this.ssiDto?.countryUrlPrefix) {
        const countryPrefix = this.ssiDto.countryUrlPrefix;
        const baseUrl = getBaseUrl(countryPrefix);
        this.algonomyClient = new AlgonomyClient({ BASE: baseUrl });
        const placements: Placement[] = this.recoLoaders
          .map((loader) => loader.ssiDto?.placement)
          .filter(neitherNullNorUndefined);
        this.richRelevanceConsent =
          await this.recoConsent.getRichRelevanceConsent();
        this.makeRenderRequest(this.getRequestBody(placements));
      }
      document.addEventListener(
        variantenWechselEvent,
        this.handleFarbwechselEvent.bind(this),
        {
          passive: true,
          signal: this.componentDisconnectedAbortController.signal,
        },
      );
    }
  }

  makeRenderRequest(requestBody: RenderRequestDto): void {
    if (this.algonomyClient) {
      const request =
        this.algonomyClient.default.postRecoRecommendationsFrontendRender(
          requestBody,
        );
      const id = setTimeout(() => request.cancel(), 3000);
      request
        .then((response) => {
          clearTimeout(id);
          AlgonomyCookieStringStorage.store(response.algonomyCookieString);
          return this.renderRecommendations.bind(this)(response.placements);
        })
        .catch(this.removePlaceHolders.bind(this));
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.recoConsent.setupQueue();
    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        this.setupRecommendationsLoading.bind(this),
        {
          signal: this.componentDisconnectedAbortController.signal,
        },
      );
    } else {
      this.setupRecommendationsLoading();
    }
  }

  disconnectedCallback(): void {
    this.componentDisconnectedAbortController.abort();
  }

  private handleFarbwechselEvent(
    event: CustomEvent<VariantenWechselDetails>,
  ): void {
    const placements = this.recoLoaders
      .map((loader) => {
        switch (loader.ssiDto?.placement?.typeTag) {
          case "ItemPageBottom1Fashion":
          case "ItemPageBottom2Fashion":
          case "ItemPageBottom1Beauty":
          case "ItemPageBottom2Beauty":
            return {
              ...loader.ssiDto.placement,
              syanFarbId: {
                syan: parseInt(event.detail.syan, 10),
                farbId: event.detail.farbId,
              },
            };
          default:
            return loader.ssiDto?.placement;
        }
      })
      .filter(neitherNullNorUndefined);

    this.makeRenderRequest(this.getRequestBody(placements));
  }

  private renderRecommendations(
    recommendationResponse: Map_String,
  ): Promise<void[]> {
    const results = this.recoLoaders.map((loader) => {
      const placement = loader.ssiDto?.placement;
      if (
        placement &&
        Object.prototype.hasOwnProperty.call(
          recommendationResponse,
          placement.typeTag,
        )
      ) {
        const recommendations = recommendationResponse[placement.typeTag];
        return loader.renderRecommendation(recommendations);
      } else {
        /* If the response does not contain any rendered content for this particular loader,
        at least the placeholder has to be removed */
        return loader.renderRecommendation("");
      }
    });
    return Promise.all(results).then((res) => {
      if (
        this.recoLoaders.some(
          (loader) => loader.ssiDto?.aktionsFlagsEnabled === true,
        )
      ) {
        document.dispatchEvent(this.campAktionsflagsInitialized);
      }
      return res;
    });
  }

  private determineLeader(): boolean {
    const index = this.recoLoaders.indexOf(this);
    return index === this.recoLoaders.length - 1;
  }

  private removePlaceHolders(): Promise<void[]> {
    return Promise.all(
      this.recoLoaders.map((loader) => loader.renderRecommendation("")),
    );
  }
}

safeDefine(recoLoaderTagName, MultiInstanceRecoLoader);
